// ISO-3166 alpha-2 codes for the residence-country (payouts) picker.
// Names are NOT stored here — render them per-locale with Intl.DisplayNames,
// so all 17 UI languages get native country names with zero translation work.
// (countries.ts is a different list: the flag-based birth-country picker with
// sub-national/territory entries that have no ISO code.)
// "XK" (Kosovo) is user-assigned but accepted by the backend validation.
//
// This list must stay EXACTLY the set `TutorProfile` validates against
// (`ISO3166::Country.codes + ["XK"]`) — it used to be a hand-curated subset,
// which silently made 40 real places (Réunion, Martinique, Jersey, Isle of
// Man, …) impossible to pick even though the server would have accepted them.
// `spec/frontend/country_codes_spec.rb` fails the build if the two drift.

export const ISO_COUNTRY_CODES = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW",
  "AX", "AZ", "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN",
  "BO", "BQ", "BR", "BS", "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD", "CF", "CG",
  "CH", "CI", "CK", "CL", "CM", "CN", "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ",
  "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE", "EG", "EH", "ER", "ES", "ET", "FI",
  "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF", "GG", "GH", "GI", "GL",
  "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HM", "HN", "HR",
  "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT", "JE", "JM",
  "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ", "LA",
  "LB", "LC", "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME",
  "MF", "MG", "MH", "MK", "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU",
  "MV", "MW", "MX", "MY", "MZ", "NA", "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP",
  "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG", "PH", "PK", "PL", "PM", "PN", "PR",
  "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW", "SA", "SB", "SC", "SD",
  "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS", "ST", "SV",
  "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO",
  "TR", "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE",
  "VG", "VI", "VN", "VU", "WF", "WS", "XK", "YE", "YT", "ZA", "ZM", "ZW",
] as const;

// Localized display name for a country code, falling back to the code itself
// when Intl lacks data (e.g. "XK" in some runtimes).
export function countryDisplayName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

// Codes sorted by their localized display name for picker rendering.
export function sortedCountryOptions(locale: string): { code: string; name: string }[] {
  return ISO_COUNTRY_CODES
    .map((code) => ({ code, name: countryDisplayName(code, locale) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}

// Cached English name per code. The pickers feed this to the search index so a
// tutor on a Spanish or Japanese UI can still type the English name they know.
let englishNames: Map<string, string> | null = null;
export function englishCountryName(code: string): string {
  if (!englishNames) {
    englishNames = new Map();
    try {
      const en = new Intl.DisplayNames(["en"], { type: "region" });
      for (const c of ISO_COUNTRY_CODES) englishNames.set(c, en.of(c) ?? c);
    } catch {
      // Intl without region data — the localized name alone still searches.
    }
  }
  return englishNames.get(code) ?? code;
}

// countries.ts persists its OWN English spelling as the stored value, and for
// ~40 entries that spelling is not the one Intl uses in English ("Ivory Coast"
// vs "Côte d'Ivoire", "Czech Republic" vs "Czechia", "Turkey" vs "Türkiye").
// Without these the reverse lookup below missed, and a French tutor saw those
// countries stuck in English while every neighbour in the list was translated.
// Only entries that really ARE the ISO territory belong here — Bonaire, Saba
// and Sint Eustatius all sit inside BQ, so mapping them would collapse three
// distinct picker rows onto one label; they stay English on purpose.
const STORED_NAME_TO_CODE: Record<string, string> = {
  "Aland Islands": "AX",
  "Antigua and Barbuda": "AG",
  "Bosnia and Herzegovina": "BA",
  "British Indian Ocean Territory": "IO",
  "Christmas Island": "CX",
  "Cocos Island": "CC",
  "Cook Islands": "CK",
  Curacao: "CW",
  "Czech Republic": "CZ",
  "Democratic Republic of the Congo": "CD",
  "East Timor": "TL",
  "Falkland Islands": "FK",
  "French Polynesia": "PF",
  Guernsey: "GG",
  "Hong Kong": "HK",
  "Isle of Man": "IM",
  "Ivory Coast": "CI",
  Jersey: "JE",
  // XK is user-assigned: some ICU builds resolve it to "Kosovo" and some hand
  // back the raw code, so pin it rather than depend on the runtime.
  Kosovo: "XK",
  Macao: "MO",
  Martinique: "MQ",
  Montserrat: "MS",
  Myanmar: "MM",
  Niue: "NU",
  "Norfolk Island": "NF",
  "Northern Mariana Islands": "MP",
  Palestine: "PS",
  "Pitcairn Islands": "PN",
  "Republic of the Congo": "CG",
  "Saint Barthélemy": "BL",
  "Saint Kitts and Nevis": "KN",
  "Saint Lucia": "LC",
  "Saint Vincent and the Grenadines": "VC",
  "São Tomé and Príncipe": "ST",
  "Sint Maarten": "SX",
  Swaziland: "SZ",
  Tokelau: "TK",
  "Trinidad and Tobago": "TT",
  Turkey: "TR",
  "Turks and Caicos": "TC",
  "Vatican City": "VA",
  "Virgin Islands": "VI",
};

// English-name → ISO-code reverse index, built once from Intl's own English
// names so countries.ts (which persists ENGLISH names as the DB value for
// countryOfBirth) can be DISPLAYED per-locale without a 260-entry hand map.
// The explicit overrides above are layered on top for the spellings Intl
// doesn't share with us, and win over Intl's own name.
let englishNameToCode: Map<string, string> | null = null;
function nameIndex(): Map<string, string> {
  if (!englishNameToCode) {
    const en = new Intl.DisplayNames(["en"], { type: "region" });
    englishNameToCode = new Map();
    for (const code of ISO_COUNTRY_CODES) {
      const name = en.of(code);
      if (name && name !== code) englishNameToCode.set(name, code);
    }
    for (const [name, code] of Object.entries(STORED_NAME_TO_CODE)) {
      englishNameToCode.set(name, code);
    }
  }
  return englishNameToCode;
}

// ISO code for a canonical ENGLISH country name as countries.ts stores it, or
// null for the sub-national/territory entries that have no code at all.
export function codeForStoredCountryName(englishName: string): string | null {
  return nameIndex().get(englishName) ?? null;
}

// Localized display for a canonical ENGLISH country name (the stored value).
// Sub-national/territory entries with no ISO code (Basque Country, Sicily, …)
// fall back to the English name — Intl has no word for them anyway.
export function localizedCountryName(englishName: string, locale: string): string {
  const code = codeForStoredCountryName(englishName);
  return code ? countryDisplayName(code, locale) : englishName;
}
